// frontend/src/pages/superadmin/PackagesManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Package, Plus, Edit2, Trash2, X, CheckCircle, Save, DollarSign,
  PlaneTakeoff, Fuel, CalendarDays, Radar, MessageSquare, CalendarCheck,
  Truck, Receipt, ClipboardList, MapPin, RefreshCw, GraduationCap,
  ShieldAlert, TrendingUp, Wrench, BarChart3, Warehouse, Users, Globe,
  ChevronDown, ChevronUp, Tag, Layers
} from 'lucide-react';
import { getPackages, createPackage, updatePackage, deletePackage } from '../../services/superAdminApi';

// ─── Groupes de modules ────────────────────────────────────────────────────────
const MODULE_GROUPS = {
  client: {
    label: 'Client Packages',
    color: '#3b82f6',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    slugs: ['client_portal', 'fuel_online', 'hangar_lease', 'maintenance', 'premium'],
  },
  core: {
    label: 'Core Modules',
    color: '#f97316',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
    slugs: ['movement_billing', 'fuel_management', 'staff_scheduling', 'live_traffic_map',
            'client_chat', 'appointments', 'fleet_management', 'invoicing_hangars'],
  },
  operations: {
    label: 'Airport Operations',
    color: '#06b6d4',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    slugs: ['inspections', 'airport_zones', 'recurring_tasks', 'certifications', 'deletion_audit'],
  },
  analytics: {
    label: 'Analytics',
    color: '#a855f7',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
    slugs: ['fuel_analytics', 'maint_analytics', 'financial_analytics', 'flight_analytics', 'hangar_analytics'],
  },
};

const getGroupForSlug = (slug) => {
  for (const [key, group] of Object.entries(MODULE_GROUPS)) {
    if (group.slugs.includes(slug)) return { key, ...group };
  }
  return { key: 'other', label: 'Other', color: '#6b7280', bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400' };
};

// ─── Icônes par slug ───────────────────────────────────────────────────────────
const MODULE_ICONS = {
  client_portal:       Globe,
  fuel_online:         Fuel,
  hangar_lease:        Warehouse,
  maintenance:         Wrench,
  premium:             Tag,
  movement_billing:    PlaneTakeoff,
  fuel_management:     Fuel,
  staff_scheduling:    CalendarDays,
  live_traffic_map:    Radar,
  client_chat:         MessageSquare,
  appointments:        CalendarCheck,
  fleet_management:    Truck,
  invoicing_hangars:   Receipt,
  inspections:         ClipboardList,
  airport_zones:       MapPin,
  recurring_tasks:     RefreshCw,
  certifications:      GraduationCap,
  deletion_audit:      ShieldAlert,
  fuel_analytics:      TrendingUp,
  maint_analytics:     Wrench,
  financial_analytics: DollarSign,
  flight_analytics:    BarChart3,
  hangar_analytics:    Warehouse,
};

// ─── Sous-composant : carte package ───────────────────────────────────────────
const PackageCard = ({ pkg, onEdit, onDelete }) => {
  const group = getGroupForSlug(pkg.name);
  const Icon = MODULE_ICONS[pkg.name] || Package;

  return (
    <div className={`bg-gray-800/50 rounded-xl border ${group.border} overflow-hidden`}>
      <div className="p-4 border-b border-gray-700/50">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${group.bg}`}>
              <Icon className="w-4 h-4" style={{ color: group.color }} />
            </div>
            <div>
              <h3 className="text-white font-semibold">{pkg.display_name}</h3>
              <p className="text-gray-500 text-xs font-mono">{pkg.name}</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => onEdit(pkg)} className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400 transition">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onDelete(pkg)} className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400 transition">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {pkg.description && (
          <p className="text-gray-400 text-xs leading-relaxed">{pkg.description}</p>
        )}
        <div className="flex gap-3">
          <div className="flex items-center gap-1 text-green-400 text-sm">
            <DollarSign className="w-3.5 h-3.5" />
            <span className="font-medium">{pkg.price_monthly || 0}/mo</span>
          </div>
          <div className="flex items-center gap-1 text-blue-400 text-sm">
            <DollarSign className="w-3.5 h-3.5" />
            <span className="font-medium">{pkg.price_yearly || 0}/yr</span>
          </div>
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className="text-gray-600">Order: {pkg.sort_order}</span>
          {pkg.is_active
            ? <span className="flex items-center gap-1 text-green-400"><CheckCircle className="w-3 h-3" /> Active</span>
            : <span className="flex items-center gap-1 text-red-400"><X className="w-3 h-3" /> Inactive</span>
          }
        </div>
      </div>
    </div>
  );
};

// ─── Sous-composant : section groupe ──────────────────────────────────────────
const GroupSection = ({ groupKey, group, packages, onEdit, onDelete }) => {
  const [collapsed, setCollapsed] = useState(false);
  const groupPkgs = packages.filter(p => group.slugs.includes(p.name));
  if (groupPkgs.length === 0) return null;

  return (
    <div className="space-y-3">
      <div
        className={`flex items-center justify-between p-3 rounded-xl ${group.bg} border ${group.border} cursor-pointer`}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4" style={{ color: group.color }} />
          <span className="font-semibold text-white">{group.label}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${group.bg} ${group.text} border ${group.border}`}>
            {groupPkgs.length} modules
          </span>
        </div>
        {collapsed
          ? <ChevronDown className="w-4 h-4 text-gray-400" />
          : <ChevronUp className="w-4 h-4 text-gray-400" />
        }
      </div>

      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-2">
          {groupPkgs.map(pkg => (
            <PackageCard key={pkg.id} pkg={pkg} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────
const PackagesManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    name: '', display_name: '', description: '',
    price_monthly: 0, price_yearly: 0, sort_order: 0, is_active: true,
  });

  useEffect(() => { loadPackages(); }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const response = await getPackages();
      setPackages(response.data || []);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.display_name) {
      alert('Name and Display Name are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingPackage) {
        await updatePackage(editingPackage.id, formData);
      } else {
        await createPackage(formData);
      }
      setShowModal(false);
      resetForm();
      await loadPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Error saving package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pkg) => {
    if (window.confirm(`Delete "${pkg.display_name}"? This cannot be undone.`)) {
      try {
        await deletePackage(pkg.id);
        await loadPackages();
      } catch (error) {
        alert('Error deleting package');
      }
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || '',
      display_name: pkg.display_name || '',
      description: pkg.description || '',
      price_monthly: pkg.price_monthly || 0,
      price_yearly: pkg.price_yearly || 0,
      sort_order: pkg.sort_order || 0,
      is_active: pkg.is_active !== false,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', display_name: '', description: '', price_monthly: 0, price_yearly: 0, sort_order: 0, is_active: true });
    setEditingPackage(null);
  };

  // Packages non classifiés dans aucun groupe connu
  const allKnownSlugs = Object.values(MODULE_GROUPS).flatMap(g => g.slugs);
  const unknownPackages = packages.filter(p => !allKnownSlugs.includes(p.name));

  const tabs = [
    { key: 'all', label: 'All' },
    ...Object.entries(MODULE_GROUPS).map(([key, g]) => ({ key, label: g.label })),
  ];

  const filteredGroups = activeTab === 'all'
    ? Object.entries(MODULE_GROUPS)
    : Object.entries(MODULE_GROUPS).filter(([key]) => key === activeTab);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Modules & Packages</h1>
          <p className="text-gray-400 text-sm">{packages.length} modules — gérer les offres vendues aux aéroports</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2 transition"
        >
          <Plus className="w-4 h-4" />
          Add Module
        </button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(MODULE_GROUPS).map(([key, group]) => {
          const count = packages.filter(p => group.slugs.includes(p.name)).length;
          return (
            <div key={key} className={`p-3 rounded-xl border ${group.border} ${group.bg}`}>
              <p className={`text-xs ${group.text} font-medium`}>{group.label}</p>
              <p className="text-2xl font-bold text-white mt-1">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Tabs filtre */}
      <div className="flex gap-2 border-b border-gray-700 pb-3 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition ${
              activeTab === tab.key
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Groupes */}
      <div className="space-y-6">
        {filteredGroups.map(([key, group]) => (
          <GroupSection
            key={key}
            groupKey={key}
            group={group}
            packages={packages}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}

        {/* Packages sans groupe (custom) */}
        {activeTab === 'all' && unknownPackages.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-700/30 border border-gray-600">
              <Package className="w-4 h-4 text-gray-400" />
              <span className="font-semibold text-white">Custom</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-600 text-gray-300">{unknownPackages.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-2">
              {unknownPackages.map(pkg => (
                <PackageCard key={pkg.id} pkg={pkg} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">
                {editingPackage ? `Edit — ${editingPackage.display_name}` : 'New Module'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Slug (name) *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono"
                    placeholder="fuel_management"
                    disabled={!!editingPackage}
                  />
                  {editingPackage && <p className="text-xs text-gray-600 mt-1">Le slug ne peut pas être modifié</p>}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Display Name *</label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="Fuel Management"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
                  placeholder="Describe what this module includes..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Monthly Price ($)</label>
                  <input
                    type="number"
                    value={formData.price_monthly}
                    onChange={(e) => setFormData({ ...formData, price_monthly: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                    min="0" step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Yearly Price ($)</label>
                  <input
                    type="number"
                    value={formData.price_yearly}
                    onChange={(e) => setFormData({ ...formData, price_yearly: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                    min="0" step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Status</label>
                  <select
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {submitting
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Save className="w-4 h-4" />
                }
                {submitting ? 'Saving...' : (editingPackage ? 'Update' : 'Create')}
              </button>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackagesManager;